import { UI_VERSION, DIRECT_DOMAIN, MSP_ENABLED, FALL_BACK_VERSION_ENABLED } from './version';
import Registry from '@reporting-n-viz/oneapp-extensibility';

const manage_app = ({
	sparkyFramework: {
		events,
		onAuthed,
		StartupManager,
		getFrameworkServiceUrl,
		dedupPromise,
		fawkesUtils: { getRedirectURL, getAuthHeaders },
		getAuthState,
		getState,
		getMainState,
		hasTsgSupport,
		dispatch,
	},
}) => {
	const getTenantTypeFromSparky = () => {
		if (
			getAuthState()?.instances?.get('prisma_access_edition_onprem') ||
			getAuthState()?.instances?.get('prisma_access_panorama')
		) {
			return StartupManager.PANORAMA_CONFIG;
		}
		return StartupManager.FAWKES_CONFIG;
	};
	const isPaEnabled = () => {
		const instances = getAuthState()?.instances;
		if (instances.has('prisma_access_edition_onprem')) {
			return instances.has('prisma_access_panorama');
		}
		if (instances.has('bulk_config')) {
			return instances.has('prisma_access_edition');
		}
		return Boolean(instances?.has('prisma_access_edition')) && Boolean(instances?.has('prisma_access'));
	};

	const isNgfwEnabled = () => {
		const instances = getAuthState()?.instances;
		if (!instances || !instances.length) {
			return false;
		}
		if (instances.has('bulk_config')) {
			return true;
		}
		return instances.has('prisma_access') && instances.has('strata_insights');
	};

	const dedupPromiseGenerator = ({ url, auth, getAuthHeaders }) => {
		return dedupPromise(
			`${url}?tsgId=${auth.tsg_id}`,
			() => {
				const controller = new AbortController();
				const timeoutId = setTimeout(() => controller.abort(), 10000);
				return fetch(url, {
					signal: controller.signal,
					withCredentials: true,
					method: 'GET',
					responseType: 'json',
					headers: getAuthHeaders(),
				})
					.then(async (response) => {
						let resp = await response.json();
						clearTimeout(timeoutId);
						return resp;
					})
					.catch((err) => {
						console.log(err);
					});
			},
			{ keepPromiseIn: 10000 },
		);
	};

	//local version set during deploy time
	let localVersion = UI_VERSION;
	let fn = async ({ auth, onTsgUnload }) => {
		//Entering the TSG
		let info = getAuthState()?.instances?.get('prisma_access');
		if (MSP_ENABLED && !info) {
			info = getAuthState()?.instances?.get('bulk_config');
		}
		let adminURL = info?.runtime_attributes?.admin_api_url;

		const type = getTenantTypeFromSparky();

		const DASLicenseFetching = () => {
			const url = new URL(
				'/subscription/v1/refresh_tenant_device_licenses',
				getFrameworkServiceUrl('api_gateway'),
			).toString();
			return dedupPromise(
				`${url}?tsgId=${auth.tsg_id}`,
				() => {
					const header = getAuthHeaders();
					const newHeader = {
						Authorization: `Bearer ${header['x-auth-jwt']}`,
					};
					return fetch(url, {
						withCredentials: true,
						method: 'GET',
						headers: newHeader,
					})
						.then((response) => {
							return response.text();
						})
						.catch((err) => {
							console.log(err);
						});
				},
				{ keepPromiseIn: 10000 },
			);
		};
		DASLicenseFetching(); // TODO: fn after version check

		fetch(adminURL + `/api/activity/login`, {
			withCredentials: true,
			method: 'GET',
			responseType: 'json',
			headers: getAuthHeaders(),
		})
			.then(async (response) => {
				console.log('Entering the TSG ID: ', auth.tsg_id, 'TSG Name: ', auth.tsg_name);
			})
			.catch((err) => {
				console.log(err);
			});

		events?.once?.('logout', () => {
			const url = new URL(`/api/activity/logout`, adminURL).toString();
			return dedupPromise(
				`${url}?tsg_id=${auth.tsg_id}`,
				() =>
					fetch(url, {
						withCredentials: true,
						method: 'GET',
						responseType: 'json',
						headers: getAuthHeaders(),
					})
						.then(async (response) => {
							console.log('Leaving the TSG ID: ', auth.tsg_id, 'TSG Name: ', auth.tsg_name);
						})
						.catch((err) => {
							console.log(err);
						}),
				{ keepPromiseIn: 10000 },
			);
		});
		if (type === StartupManager.PANORAMA_CONFIG) {
			dedupPromiseGenerator({
				url: new URL('/api/extensions/gpcs/get/get_tenant_subtenant_info', getRedirectURL()).toString(),
				auth,
				getAuthHeaders,
			})
				.then((response) => {
					return response.text();
				})
				.catch((err) => {
					console.log(err);
				});
		}

		const getMigrationStatusInfo = dedupPromiseGenerator({
			url: new URL('/api/migration/status', getRedirectURL()).toString(),
			auth,
			getAuthHeaders,
		});

		const getNotificationsInfo = dedupPromiseGenerator({
			url: new URL('/api/v1/notifications?&readStatus=false', adminURL).toString(),
			auth,
			getAuthHeaders,
		});

		const getFawkesLicenseInfo = dedupPromiseGenerator({
			url: new URL('/api/extensions/gpcs/license', getRedirectURL()).toString(),
			auth,
			getAuthHeaders,
		});

		const getGpEPPromise = dedupPromiseGenerator({
			url: new URL(
				'/api/config/v9.2/configByPath?type=container&folder=Mobile%20Users%20Container&prefix=plugins/cloud_services&subPath=enable',
				getRedirectURL(),
			).toString(),
			auth,
			getAuthHeaders,
		});

		const getFeaturesPromise = dedupPromiseGenerator({
			url: new URL('/api/features', adminURL).toString(),
			auth,
			getAuthHeaders,
		});

		/********** decouple sparky call*/
		const instances = getAuthState()?.instances;
		const extensions = [];

		instances.forEach((instance) => {
			const ext = {
				id: instance?.tenant_id,
				active: instance?.status === 'running',
				info: JSON.stringify(instance),
				internalTenantId: MSP_ENABLED
					? instances?.get('bulk_config')?.tenant_id || instances?.get('prisma_access')?.tenant_id
					: instances?.get('prisma_access')?.tenant_id,
				name: instance?.app_id,
				supportAccountId: instance?.support_account_id,
			};
			extensions.push(ext);
		});

		info.tsg_instances = instances;

		const authInfo = {
			credentials: getAuthState()?.user,
			entitlements: getAuthState()?.instances,
			extraInfo: {
				appPortal: getFrameworkServiceUrl('app_portal'),
			},
		};
		const loggedInUser = {
			email: getAuthState()?.user?.email,
			currentInstanceId: MSP_ENABLED
				? getAuthState()?.instances?.get('bulk_config')?.tenant_id || getAuthState()?.instances?.get('prisma_access')?.tenant_id
				: getAuthState()?.instances?.get('prisma_access')?.tenant_id,
			currentSupportAccountId: MSP_ENABLED
				? getAuthState()?.instances?.get('bulk_config')?.support_account_id || getAuthState()?.instances?.get('prisma_access')?.support_account_id
				: getAuthState()?.instances?.get('prisma_access')?.support_account_id,
			currentTsgId: getAuthState()?.tsg_id,
			profile: getAuthState()?.user,
			supportAccountIds: getAuthState()?.user?.supportAccountIds,
			supportaccountids: getAuthState()?.user?.supportaccountids,
			userName: getAuthState()?.user?.username,
		};
		const cluster = info?.runtime_attributes?.cluster;
		cluster.redirectURL = getRedirectURL();
		const tenant = {
			info,
			cluster: cluster,
			clusterName: info?.runtime_attributes?.cluster?.name,
			type,
			id: info?.tenant_id,
			tenantId: info?.tenant_id,
			supportAccountId: info?.support_account_id,
			sgId: info?.tsg_id,
			accountName: info?.support_account_name,
			iamControlled: info?.iam_controlled,
			state: info.status,
			extensions,
			pa_enabled: isPaEnabled(),
			ngfw_enabled: isNgfwEnabled(),
		};
		loggedInUser.tenant = tenant;
		if (type !== StartupManager.PANORAMA_CONFIG) {
			await Promise.all([
				getFawkesLicenseInfo,
				getGpEPPromise,
				getMigrationStatusInfo,
				getNotificationsInfo,
				getFeaturesPromise,
			])
				.then((response) => {
					const fawkesMain = {
						licenseInfo: response?.[0],
						gpEpEnabled: response?.[1]?.result?.enable,
						migrationStatus: response?.[2]?.state,
						migrationNewFawkesTenantId: response?.[2]?.fawkesTenantId,
						notificationsInfo: response?.[3],
						featuresInfo: response?.[4],
						loggedInUser,
						tenant,
						authInfo,
						tokenInfo: getState()?.libTokens,
					};
					dispatch({
						type: 'set-values',
						values: {
							fawkesMain: fawkesMain,
						},
					});
				})
				.catch((e) => console.error(e));
		} else {
			await Promise.all([getMigrationStatusInfo, getNotificationsInfo, getFeaturesPromise, getFawkesLicenseInfo])
				.then((response) => {
					const fawkesMain = {
						licenseInfo: response?.[3],
						migrationStatus: response?.[0]?.state,
						migrationNewFawkesTenantId: response?.[0]?.fawkesTenantId,
						notificationsInfo: response?.[1],
						featuresInfo: response?.[2],
						tenant,
						loggedInUser,
						tokenInfo: getState()?.libTokens,
					};
					dispatch({
						type: 'set-values',
						values: {
							fawkesMain: fawkesMain,
						},
					});
				})
				.catch((e) => console.error(e));
		}

		/********** decouple sparky call*/

		return Promise.all([
			import(/* webpackPreload: true, webpackChunkName: "predef" */ './predefined'),
			import(/* webpackPreload: true */ './i18n'),
		])
			.then(() => import(/* webpackPreload: true */ './routes'))
			.then((r) => r.default);
	};
	// return (params) => fn(params);
	const contributeExtension = () => {
		const extensionKey = 'aiops.posture_settings';
		const path = '/security-checks-cm';
		const subKey = `${path}-active`;
		const extensionId = 'aiops-ngfw-security-checks-cm-active';

		const postureSettingsExtensions = Registry.getExtensions(extensionKey) || [];
		const existingExtension = postureSettingsExtensions.find((ext) => ext.key === subKey);
		if (existingExtension) {
			return;
		}
		try {
			Registry.contributeExtension(extensionKey, {
				id: extensionId,
				config: {
					key: subKey,
					path,
					name: {
						defaultMessage: 'Security Checks',
					},
					component: () => import('./containers/securityChecks/index'),
					position: ['start'],
				},
			});
		} catch (error) {
			console.error('Failed to contribute extension', error);
		}
	};
	//Need to handle MSP case
	if (hasTsgSupport() && !DIRECT_DOMAIN && !MSP_ENABLED) {
		return (params) => {
			if (!getAuthState()?.instances?.isFawkesRunning) {
				// no running fawkes
				if (FALL_BACK_VERSION_ENABLED) {
					contributeExtension();
				}
				return null;
			}
			//detect the version
			let version = getAuthState()?.instances?.get('prisma_access')?.runtime_attributes?.version;
			console.log('version check - Sparky Version: ', version, ' - LocalVersion:', localVersion);
			if (localVersion === version) {
				console.log('Loading Version: ', localVersion);
				contributeExtension();
				//varsion match. Load this site
				return fn(params);
			} else {
				//return null
				return null;
			}
		};
	} else {
		//Non TSG or MSP case
		if (MSP_ENABLED) {
			//MSP tenant
			//detect the version
			return (params) => {
				let version = getAuthState()?.instances?.get('bulk_config')?.runtime_attributes?.version;
				if (!version) {
					console.log('MSP PA Root Tenant - Fetching version from prisma_access');
					version = getAuthState()?.instances?.get('prisma_access')?.runtime_attributes?.version;
				}
				console.log('MSP version check - Sparky Version: ', version, ' - LocalVersion:', localVersion);
				if (localVersion === version) {
					console.log('MSP Loading Version: ', localVersion);
					//varsion match. Load this site
					return fn(params);
				} else {
					return null;
				}
			};
		} else {
			//Non TSG or MSP case
			contributeExtension();
			console.log('Loading Cloud Management for NonTsg or Direct Domain');
			return (params) => fn(params);
		}
	}
};
// manage_app.exposes = {
//     default: {
//         responsePage: () => import('./exposedFunctions.js'),
//     }
// }
export default manage_app;
